import { useState } from "react";
import { Loader2, AlertTriangle, Pause, X } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";

const LOSSES = [
  "Live deal pipeline & Quick Log",
  "Push alerts (hot deals, 24h stall, red CSI)",
  "Manager dashboard & team leaderboard",
  "CSI follow-ups sent to your customers",
];

export function CancelSubscriptionDialog({ open, onOpenChange, onPauseInstead }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPauseInstead?: () => void;
}) {
  const { subscription, refresh } = useSubscription();
  const [canceling, setCanceling] = useState(false);

  const periodEnd = subscription?.current_period_end
    ? new Date(subscription.current_period_end).toLocaleDateString(undefined, { dateStyle: "long" })
    : null;

  const cancel = async () => {
    setCanceling(true);
    const { data, error } = await supabase.functions.invoke("subscription-cancel", {
      body: { environment: getPaddleEnvironment() },
    });
    setCanceling(false);
    if (error || data?.error) {
      toast.error("Could not cancel", { description: data?.error ?? error?.message });
      return;
    }
    toast.success("Cancellation scheduled", {
      description: periodEnd ? `You keep access until ${periodEnd}.` : "You keep access until your period ends.",
    });
    await refresh();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <DialogTitle className="text-center">Cancel your subscription?</DialogTitle>
          <DialogDescription className="text-center">
            {periodEnd
              ? <>You'll keep full access until <span className="font-medium text-foreground">{periodEnd}</span>, then you'll lose:</>
              : <>You'll keep access until the end of your billing period, then you'll lose:</>}
          </DialogDescription>
        </DialogHeader>

        <ul className="space-y-1.5 rounded-md border border-border bg-muted/30 p-3 text-sm">
          {LOSSES.map((l) => (
            <li key={l} className="flex items-start gap-2">
              <X className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
              <span className="text-muted-foreground">{l}</span>
            </li>
          ))}
        </ul>

        {onPauseInstead && (
          <button
            type="button"
            onClick={() => { onOpenChange(false); onPauseInstead(); }}
            className="w-full rounded-md border border-primary/40 bg-primary/5 p-3 text-left hover:bg-primary/10 transition-colors"
          >
            <div className="flex items-start gap-2">
              <Pause className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-medium">Pause instead — keep your data</p>
                <p className="text-xs text-muted-foreground">Stop billing for 1–3 months and pick back up where you left off.</p>
              </div>
            </div>
          </button>
        )}

        <DialogFooter className="sm:flex-row-reverse gap-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={canceling}>Keep subscription</Button>
          <Button variant="destructive" onClick={cancel} disabled={canceling}>
            {canceling && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Cancel anyway
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}